/**
 * WebSocket 客户端封装模块
 * 管理 WebSocket 连接、消息收发、自动重连和心跳保活
 */
import type { ClientMessage, ServerMessage } from '@fluxlab/protocols';

export type { ClientMessage };

/** 消息处理回调类型 */
type MessageHandler = (msg: ServerMessage) => void;

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = `${WS_PROTOCOL}//${window.location.host}/ws`;
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000];
const HEARTBEAT_INTERVAL = 15000;
const PING_TIMEOUT = 5000;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pingTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private _onStateChange: ((state: 'connected' | 'disconnected' | 'connecting') => void) | null = null;

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get connecting(): boolean {
    return this.ws?.readyState === WebSocket.CONNECTING;
  }

  onStateChange(cb: ((state: 'connected' | 'disconnected' | 'connecting') => void) | null): void {
    this._onStateChange = cb;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.reconnectAttempt < 0) this.reconnectAttempt = 0;

    this._onStateChange?.('connecting');

    try {
      this.ws = new WebSocket(WS_URL);
    } catch (e) {
      console.error('[WS] Failed to create WebSocket:', e);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log('[WS] Connected');
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.reconnectAttempt = 0;
      this._onStateChange?.('connected');
      this.startHeartbeat();
    };

    this.ws.onclose = (event) => {
      console.log(`[WS] Disconnected (code: ${event.code}, reason: ${event.reason})`);
      this.stopHeartbeat();
      this._onStateChange?.('disconnected');
      this.scheduleReconnect();
    };

    this.ws.onerror = (event) => {
      console.error('[WS] Error:', event);
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: ServerMessage = JSON.parse(event.data as string);
        if (msg.type === 'pong') {
          if (this.pingTimeoutTimer) {
            clearTimeout(this.pingTimeoutTimer);
            this.pingTimeoutTimer = null;
          }
          return;
        }
        this.dispatch(msg);
      } catch (e) {
        console.error('[WS] Failed to parse message:', e);
      }
    };
  }

  disconnect(): void {
    this.reconnectAttempt = -1; // prevent reconnection
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this._onStateChange?.('disconnected');
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn('[WS] Cannot send, not connected:', msg.type);
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  private dispatch(msg: ServerMessage): void {
    for (const handler of this.handlers) {
      try {
        handler(msg);
      } catch (e) {
        console.error('[WS] Handler error:', e);
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempt < 0 || this.reconnectTimer) return;
    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    this.reconnectAttempt++;
    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping', timestamp: Date.now() });
        if (this.pingTimeoutTimer) clearTimeout(this.pingTimeoutTimer);
        this.pingTimeoutTimer = setTimeout(() => {
          console.warn('[WS] Ping timeout, closing connection');
          this.ws?.close();
        }, PING_TIMEOUT);
      }
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pingTimeoutTimer) {
      clearTimeout(this.pingTimeoutTimer);
      this.pingTimeoutTimer = null;
    }
  }
}

/** 全局 WebSocket 客户端单例：连接管理、消息收发、自动重连与心跳保活 */
export const wsClient = new WebSocketClient();
